import { z } from "zod";
import { authorityLevelEnum, findingTypeEnum } from "./types/enums.js";
import { mcpEnvelopeSchema } from "./mcp-envelope.js";

// ============================================================
// MCP output-schema registry (envelope)
//
// Single source of truth for every migrated tool's wire shape.
// Each tool's registration references the wrapped output schema —
// `<Name>OutputSchema` — which composes the bare data fields with
// the standard envelope `_meta` field via `mcpEnvelopeSchema()`.
// Tools register with `<Name>OutputSchema.shape`; the MCP SDK then
// validates `structuredContent` against the FULL wire shape
// (data + _meta), which is what we actually emit.
//
// Pattern per tool:
//   const FooDataSchema = z.object({ ...data fields... });
//   export const FooOutputSchema = mcpEnvelopeSchema(FooDataSchema);
//   export type FooData = z.infer<typeof FooDataSchema>;
//   export type FooOutput = z.infer<typeof FooDataSchema>;
//
// Handlers build the bare `FooData` and pass it to `makeEnvelope`,
// which adds `_meta` to produce the full `FooOutput`. The SDK
// validates the emitted structuredContent against `FooOutputSchema`
// — drift between the data fields the handler builds and what the
// schema documents surfaces as a runtime error at the MCP boundary.
//
// The schema registry served at `iamu://schemas/<Name>` exposes
// the wrapped (full wire) shape so external agents fetching it see
// `_meta` documented.
// ============================================================

// ------------------------------------------------------------
// Shared sub-types
// ------------------------------------------------------------

// AuthorityLevel + FindingType live in ./types/enums.js — reuse, don't duplicate.
export const AuthorityLevelSchema = authorityLevelEnum;
export const FindingTypeSchema = findingTypeEnum;

export const MirrorStatusSchema = z.enum([
  "synced",
  "pending_retry",
  "local_only",
  "not_applicable",
]);
export type MirrorStatus = z.infer<typeof MirrorStatusSchema>;

/** Summary row returned by `list_findings`. Excludes full body
 *  fields (ui_fact, consequence, citations) — those require
 *  `get_finding` with the resource-link URI. */
export const FindingSummarySchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  title: z.string(),
  type: FindingTypeSchema,
  /** Discriminator: "canonical" for deduplicated cloud findings,
   *  "observation" for raw walkthrough findings (local or cloud). */
  kind: z.enum(["canonical", "observation"]),
  severity: z
    .enum(["critical", "major", "minor", "nit"])
    .nullable()
    .describe("Effective severity. Null when not yet assigned."),
  triage_status: z
    .string()
    .describe("open | addressed | deferred | wont_fix | dismissed"),
  /** First 200 chars of the finding description. Fetch get_finding for the full text. */
  description: z.string(),
  walkthrough_id: z.string().nullable(),
  scenario_id: z.string(),
  created_at: z.string().datetime(),
  canonical_id: z.string().nullable(),
  cloud_mirrored: z.boolean(),
});
export type FindingSummary = z.infer<typeof FindingSummarySchema>;

/** Summary row returned by `list_scenarios`. Excludes full brief
 *  text — available via `get_scenario`. */
export const ScenarioSummarySchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  outline_id: z.string(),
  outline_slug: z.string().nullable(),
  persona_id: z.string(),
  persona_slug: z.string().nullable(),
  created_at: z.string().datetime(),
  baseline_score: z.number().nullable(),
  current_score: z.number().nullable(),
  target_score: z.number().nullable(),
  walkthrough_count: z.number().int().nonnegative(),
  latest_walkthrough_id: z.string().nullable(),
  cloud_mirrored: z.boolean(),
});
export type ScenarioSummary = z.infer<typeof ScenarioSummarySchema>;

// ------------------------------------------------------------
// Per-tool output schemas (the `data` field of the envelope)
// ------------------------------------------------------------

export const GetConstraintsDataSchema = z.object({
  domain: z.string(),
  constraint_count: z.number().int().nonnegative(),
  constraints: z.array(
    z.object({
      rule: z.string(),
      authority_level: AuthorityLevelSchema,
      source_name: z.string(),
      source_section: z.string().nullable().optional(),
      source_url: z.string().nullable().optional(),
      source_version: z.number(),
      source_uri: z
        .string()
        .describe("iamu://source/<id>@v<version>")
        .optional(),
    }),
  ),
  gaps: z
    .array(
      z.object({
        type: z.enum(["no_results", "low_relevance", "unverified_only"]),
        query: z.string(),
        suggestion: z.string(),
      }),
    )
    .optional(),
  context_hint_needed: z.boolean().optional(),
});
export const GetConstraintsOutputSchema = mcpEnvelopeSchema(
  GetConstraintsDataSchema,
);
export type GetConstraintsData = z.infer<typeof GetConstraintsDataSchema>;

export const QueryDataSchema = z.object({
  results: z.array(
    z.object({
      content: z.string(),
      authority_level: AuthorityLevelSchema,
      source_name: z.string(),
      source_section: z.string().nullable().optional(),
      source_url: z.string().nullable().optional(),
      source_version: z.number(),
      page_url: z.string().nullable().optional(),
      relevance_score: z.number().optional(),
      // Legacy / extended fields for backward compatibility
      chunk_id: z.string().optional(),
      context_header: z.string().optional(),
      context_text: z.string().nullable().optional(),
      source_id: z.string().optional(),
      source_uri: z.string().optional(),
      score: z.number().optional(),
      verified: z.boolean().optional(),
      trust: z.enum(["hard", "soft", "unknown"]).optional(),
    }),
  ),
  total_results: z.number().int().nonnegative().optional(),
  result_count: z.number().int().nonnegative().optional(),
  query_language: z.string().optional(),
  query_time_ms: z.number().int().nonnegative().optional(),
  gaps: z
    .array(
      z.object({
        type: z.enum(["no_results", "low_relevance", "unverified_only"]),
        query: z.string(),
        suggestion: z.string(),
      }),
    )
    .optional(),
  context_hint_needed: z.boolean().optional(),
});
export const QueryOutputSchema = mcpEnvelopeSchema(QueryDataSchema);
export type QueryData = z.infer<typeof QueryDataSchema>;

export const ValidateDataSchema = z.object({
  status: z.enum(["pass", "fail", "partial", "insufficient_coverage"]),
  satisfied: z.array(
    z.object({
      constraint: z.string(),
      source: z.string(),
      source_version: z.number().optional(),
      confidence: z.number().optional(),
    }),
  ),
  violated: z.array(
    z.object({
      constraint: z.string(),
      source: z.string(),
      source_version: z.number().optional(),
      reason: z.string(),
      suggestion: z.string().optional(),
    }),
  ),
  not_assessed: z.array(
    z.object({
      constraint: z.string(),
      source: z.string(),
      reason: z.string(),
    }),
  ),
});
export const ValidateOutputSchema = mcpEnvelopeSchema(ValidateDataSchema);
export type ValidateData = z.infer<typeof ValidateDataSchema>;

export const ListSourcesDataSchema = z.object({
  domains: z.array(
    z.object({
      tag: z.string(),
      description: z.string().nullable().optional(),
      source_count: z.number().int().nonnegative(),
      authority_levels: z.array(z.string()),
      latest_update: z.string().nullable().optional(),
    }),
  ),
  total_sources: z.number().int().nonnegative(),
  total_chunks: z.number().int().nonnegative(),
});
export const ListSourcesOutputSchema = mcpEnvelopeSchema(ListSourcesDataSchema);
export type ListSourcesData = z.infer<typeof ListSourcesDataSchema>;

export const ListFindingsDataSchema = z.object({
  findings: z.array(FindingSummarySchema),
  total_returned: z.number().int().nonnegative(),
});
export const ListFindingsOutputSchema = mcpEnvelopeSchema(
  ListFindingsDataSchema,
);
export type ListFindingsData = z.infer<typeof ListFindingsDataSchema>;

export const ListScenariosDataSchema = z.object({
  scenarios: z.array(ScenarioSummarySchema),
});
export const ListScenariosOutputSchema = mcpEnvelopeSchema(
  ListScenariosDataSchema,
);
export type ListScenariosData = z.infer<typeof ListScenariosDataSchema>;

export const GetProjectDataSchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  name: z.string(),
  cloud_linked: z.boolean(),
  cloud_project_id: z.string().nullable(),
  created_at: z.string().datetime().nullable(),
  capabilities_count: z.number().int().nonnegative(),
  outlines_count: z.number().int().nonnegative(),
  scenarios_count: z.number().int().nonnegative(),
  walkthroughs_count: z.number().int().nonnegative(),
  personas_count: z.number().int().nonnegative(),
});
export const GetProjectOutputSchema = mcpEnvelopeSchema(GetProjectDataSchema);
export type GetProjectData = z.infer<typeof GetProjectDataSchema>;

export const CreateScenarioDataSchema = z.object({
  scenario_id: z.string(),
  outline_id: z.string(),
  persona_id: z.string(),
  was_existing: z.boolean(),
  created_at: z.string().datetime(),
  brief_generated_at: z.string().datetime().nullable(),
});
export const CreateScenarioOutputSchema = mcpEnvelopeSchema(
  CreateScenarioDataSchema,
);
export type CreateScenarioData = z.infer<typeof CreateScenarioDataSchema>;

export const PrepareWalkthroughDataSchema = z.object({
  walkthrough_id: z.string(),
  scenario_id: z.string(),
  target_url: z.string(),
  lenses: z.array(z.string()),
  persona: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string().nullable(),
  }),
  outline: z.object({
    id: z.string(),
    slug: z.string().nullable(),
    title: z.string(),
  }),
  mode: z.enum(["local", "cloud"]),
  cloud_mirrored: z.boolean(),
  mirror_status: MirrorStatusSchema,
  run_dir: z.string().nullable(),
  /** Persona-specific first-person brief — markdown body of the scenario file. */
  brief: z.string(),
  /** Full composed walking prompt: persona + outline + lenses +
   *  evaluation philosophy. This is the text the walker should adopt
   *  before driving the browser; `brief` alone lacks the philosophy
   *  + lens guidance. */
  prompt_text: z.string(),
  /** Withheld during walker stage (always empty here). Populated by
   *  `get_judge_input` once the walk completes. */
  human_tests: z.array(z.string()),
});
export const PrepareWalkthroughOutputSchema = mcpEnvelopeSchema(
  PrepareWalkthroughDataSchema,
);
export type PrepareWalkthroughData = z.infer<
  typeof PrepareWalkthroughDataSchema
>;

// ============================================================
// Tier 1 — shared sub-types
// ============================================================

/** Summary row returned by `list_personas`. Excludes long-form
 *  background body — fetch via `get_persona`. */
export const PersonaSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  role: z.string(),
  /** First 200 chars of context/background. */
  context_preview: z.string().nullable(),
  status: z.enum(["active", "archived"]),
  created_at: z.string().datetime(),
  cloud_mirrored: z.boolean(),
});
export type PersonaSummary = z.infer<typeof PersonaSummarySchema>;

/** Summary row returned by `list_outlines`. Excludes narrative body. */
export const OutlineSummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  primary_capability_id: z.string().nullable(),
  primary_capability_slug: z.string().nullable(),
  /** Capability slugs (denormalized for agent display). */
  additional_capabilities: z.array(z.string()),
  task_criticality: z.string().nullable(),
  created_at: z.string().datetime(),
  cloud_mirrored: z.boolean(),
});
export type OutlineSummary = z.infer<typeof OutlineSummarySchema>;

/** Summary row returned by `list_capabilities`. */
export const CapabilitySummarySchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Number of outlines with this capability as primary. Cloud-only;
   *  null in local mode (filesystem walk would be too expensive). */
  outline_count: z.number().int().nonnegative().nullable(),
  created_at: z.string().datetime(),
  cloud_mirrored: z.boolean(),
});
export type CapabilitySummary = z.infer<typeof CapabilitySummarySchema>;

// ============================================================
// Tier 1 — list / get full-body schemas
// ============================================================

export const ListPersonasDataSchema = z.object({
  personas: z.array(PersonaSummarySchema),
});
export const ListPersonasOutputSchema = mcpEnvelopeSchema(
  ListPersonasDataSchema,
);
export type ListPersonasData = z.infer<typeof ListPersonasDataSchema>;

export const ListOutlinesDataSchema = z.object({
  outlines: z.array(OutlineSummarySchema),
});
export const ListOutlinesOutputSchema = mcpEnvelopeSchema(
  ListOutlinesDataSchema,
);
export type ListOutlinesData = z.infer<typeof ListOutlinesDataSchema>;

export const ListCapabilitiesDataSchema = z.object({
  capabilities: z.array(CapabilitySummarySchema),
});
export const ListCapabilitiesOutputSchema = mcpEnvelopeSchema(
  ListCapabilitiesDataSchema,
);
export type ListCapabilitiesData = z.infer<typeof ListCapabilitiesDataSchema>;

export const GetPersonaDataSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  role: z.string(),
  context: z.string().nullable(),
  disposition: z.string().nullable(),
  brief: z.string().nullable(),
  status: z.enum(["active", "archived"]),
  body: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().nullable(),
  cloud_mirrored: z.boolean(),
  resource_uri: z.string(),
});
export const GetPersonaOutputSchema = mcpEnvelopeSchema(GetPersonaDataSchema);
export type GetPersonaData = z.infer<typeof GetPersonaDataSchema>;

export const GetOutlineDataSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  narrative: z.string(),
  primary_capability_id: z.string().nullable(),
  primary_capability_slug: z.string().nullable(),
  additional_capabilities: z.array(z.string()),
  human_tests: z.array(z.object({ id: z.string(), question: z.string() })),
  tension: z.string().nullable(),
  situation_roles: z
    .array(z.object({ role: z.string(), stake: z.string() }))
    .nullable(),
  task_criticality: z.string().nullable(),
  complexity_level: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().nullable(),
  cloud_mirrored: z.boolean(),
  resource_uri: z.string(),
});
export const GetOutlineOutputSchema = mcpEnvelopeSchema(GetOutlineDataSchema);
export type GetOutlineData = z.infer<typeof GetOutlineDataSchema>;

export const GetFindingDataSchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  title: z.string().nullable(),
  type: FindingTypeSchema,
  severity: z.enum(["critical", "major", "minor", "nit"]).nullable(),
  kind: z.enum(["canonical", "observation"]),
  ui_fact: z.string().nullable(),
  consequence: z.string().nullable(),
  description: z.string().nullable(),
  citations: z.array(z.string()),
  walkthrough_id: z.string().nullable(),
  scenario_id: z.string(),
  persona_id: z.string().nullable(),
  run: z.string().nullable(),
  triage_status: z.string(),
  triage_note: z.string().nullable(),
  fix_status: z.string(),
  lens: z.string().nullable(),
  compliance_impact: z.string().nullable(),
  task_impact: z.string().nullable(),
  confidence: z.string().nullable(),
  regulatory_weight: z.string().nullable(),
  human_test_id: z.string().nullable(),
  walkthrough_step_id: z.string().nullable(),
  canonical_id: z.string().nullable(),
  recorded_at: z.string().datetime().nullable(),
  updated_at: z.string().datetime().nullable(),
  cloud_mirrored: z.boolean(),
  mirror_status: MirrorStatusSchema,
  resource_uri: z.string(),
});
export const GetFindingOutputSchema = mcpEnvelopeSchema(GetFindingDataSchema);
export type GetFindingData = z.infer<typeof GetFindingDataSchema>;

// ============================================================
// Tier 1 — create_* binding-state schemas
// ============================================================

export const CreatePersonaDataSchema = z.object({
  persona_id: z.string(),
  slug: z.string(),
  was_existing: z.boolean(),
  created_at: z.string().datetime(),
  brief_generated_at: z.string().datetime().nullable(),
});
export const CreatePersonaOutputSchema = mcpEnvelopeSchema(
  CreatePersonaDataSchema,
);
export type CreatePersonaData = z.infer<typeof CreatePersonaDataSchema>;

export const CreateOutlineDataSchema = z.object({
  outline_id: z.string(),
  slug: z.string(),
  primary_capability_id: z.string().nullable(),
  primary_capability_slug: z.string().nullable(),
  was_existing: z.boolean(),
  created_at: z.string().datetime(),
});
export const CreateOutlineOutputSchema = mcpEnvelopeSchema(
  CreateOutlineDataSchema,
);
export type CreateOutlineData = z.infer<typeof CreateOutlineDataSchema>;

export const CreateCapabilityDataSchema = z.object({
  capability_id: z.string(),
  slug: z.string(),
  was_existing: z.boolean(),
  created_at: z.string().datetime(),
});
export const CreateCapabilityOutputSchema = mcpEnvelopeSchema(
  CreateCapabilityDataSchema,
);
export type CreateCapabilityData = z.infer<typeof CreateCapabilityDataSchema>;

// ============================================================
// Tier 1 — walker / judge inner-loop schemas
// ============================================================

export const RecordStepDataSchema = z.object({
  step_id: z.string(),
  step_index: z.number().int().nonnegative(),
  walkthrough_id: z.string(),
  persona_reasoning: z.string(),
  recorded_at: z.string().datetime(),
  /** Set when a screenshot_path was provided. */
  screenshot_uri: z.string().nullable(),
  /** Capture failure modes (per-field) — see fingerprint shape in
   *  `packages/shared/src/types/fingerprint.ts`. Optional; null
   *  when fingerprint capture didn't run. */
  fingerprint: z
    .object({
      dom_hash: z.string().nullable(),
      ax_hash: z.string().nullable(),
      screenshot_hash: z.string().nullable(),
      url_template: z.string().nullable(),
    })
    .nullable(),
});
export const RecordStepOutputSchema = mcpEnvelopeSchema(RecordStepDataSchema);
export type RecordStepData = z.infer<typeof RecordStepDataSchema>;

export const RecordFindingDataSchema = z.object({
  finding_id: z.string(),
  slug: z.string().nullable(),
  type: FindingTypeSchema,
  severity: z.enum(["critical", "major", "minor", "nit"]).nullable(),
  kind: z.literal("observation"),
  ui_fact: z.string(),
  consequence: z.string(),
  citations: z.array(z.string()),
  scenario_id: z.string(),
  walkthrough_id: z.string(),
  persona_id: z.string().nullable(),
  triage_status: z.literal("open"),
  fix_status: z.literal("open"),
  lens: z.string().nullable(),
  recorded_at: z.string().datetime(),
  resource_uri: z.string(),
});
export const RecordFindingOutputSchema = mcpEnvelopeSchema(
  RecordFindingDataSchema,
);
export type RecordFindingData = z.infer<typeof RecordFindingDataSchema>;

/** complete_walkthrough returns the full canonical walkthrough document.
 *  The shape is governed by `CanonicalWalkthrough` in
 *  `packages/shared/src/types/walkthrough-canonical.ts` — keep that as
 *  the source of truth; this wraps it in a runtime-validated Zod shape
 *  for outputSchema declaration. Fields are intentionally permissive
 *  (z.unknown for nested arrays) — full strict-validation lives in
 *  the canonical writer. */
export const CompleteWalkthroughDataSchema = z.object({
  canonical_version: z.literal("1.0.0"),
  run_id: z.string(),
  scenario: z.object({}).passthrough(),
  target: z.object({}).passthrough(),
  provenance: z.object({}).passthrough(),
  findings: z.array(z.unknown()),
  steps: z.array(z.unknown()),
  human_tests: z.array(z.unknown()),
  human_test_overall: z.object({}).passthrough(),
  errors: z.array(z.unknown()),
  cloud_mirror_state: z.enum([
    "pushed",
    "enqueued",
    "local-only",
    "already_pushed",
    "skipped",
  ]),
});
export const CompleteWalkthroughOutputSchema = mcpEnvelopeSchema(
  CompleteWalkthroughDataSchema,
);
export type CompleteWalkthroughData = z.infer<
  typeof CompleteWalkthroughDataSchema
>;

export const GetJudgeInputDataSchema = z.object({
  walkthrough_id: z.string(),
  judge_prompt: z.object({
    id: z.string(),
    version: z.string(),
    text: z.string(),
  }),
  scenario: z.object({}).passthrough(),
  outline_context: z.object({}).passthrough(),
  steps: z.array(z.unknown()),
  findings: z.array(z.unknown()),
  human_tests: z.array(
    z.object({
      id: z.string(),
      question: z.string(),
    }),
  ),
});
export const GetJudgeInputOutputSchema = mcpEnvelopeSchema(
  GetJudgeInputDataSchema,
);
export type GetJudgeInputData = z.infer<typeof GetJudgeInputDataSchema>;

export const RecordJudgeResultDataSchema = z.object({
  walkthrough_id: z.string(),
  persisted_at: z.string().datetime(),
  canonical_path: z.string(),
  report_path: z.string(),
});
export const RecordJudgeResultOutputSchema = mcpEnvelopeSchema(
  RecordJudgeResultDataSchema,
);
export type RecordJudgeResultData = z.infer<typeof RecordJudgeResultDataSchema>;

// ------------------------------------------------------------
// Registry: name → Zod schema
// ------------------------------------------------------------

export const SCHEMA_REGISTRY = {
  // Shared sub-types — fetchable for agents that want to cross-validate
  AuthorityLevel: AuthorityLevelSchema,
  FindingType: FindingTypeSchema,
  MirrorStatus: MirrorStatusSchema,
  FindingSummary: FindingSummarySchema,
  ScenarioSummary: ScenarioSummarySchema,
  PersonaSummary: PersonaSummarySchema,
  OutlineSummary: OutlineSummarySchema,
  CapabilitySummary: CapabilitySummarySchema,
  // Per-tool output schemas (mcp-response-envelope-v2 beachhead)
  GetConstraintsData: GetConstraintsOutputSchema,
  QueryData: QueryOutputSchema,
  ValidateData: ValidateOutputSchema,
  ListSourcesData: ListSourcesOutputSchema,
  ListFindingsData: ListFindingsOutputSchema,
  ListScenariosData: ListScenariosOutputSchema,
  GetProjectData: GetProjectOutputSchema,
  CreateScenarioData: CreateScenarioOutputSchema,
  PrepareWalkthroughData: PrepareWalkthroughOutputSchema,
  // Per-tool output schemas (Tier 1 — list/get + create + walker/judge)
  ListPersonasData: ListPersonasOutputSchema,
  ListOutlinesData: ListOutlinesOutputSchema,
  ListCapabilitiesData: ListCapabilitiesOutputSchema,
  GetPersonaData: GetPersonaOutputSchema,
  GetOutlineData: GetOutlineOutputSchema,
  GetFindingData: GetFindingOutputSchema,
  CreatePersonaData: CreatePersonaOutputSchema,
  CreateOutlineData: CreateOutlineOutputSchema,
  CreateCapabilityData: CreateCapabilityOutputSchema,
  RecordStepData: RecordStepOutputSchema,
  RecordFindingData: RecordFindingOutputSchema,
  CompleteWalkthroughData: CompleteWalkthroughOutputSchema,
  GetJudgeInputData: GetJudgeInputOutputSchema,
  RecordJudgeResultData: RecordJudgeResultOutputSchema,
} as const satisfies Record<string, z.ZodType>;

export type SchemaName = keyof typeof SCHEMA_REGISTRY;

/** Resource URI an agent fetches to get a single schema's JSON. */
export function schemaResourceUri(name: SchemaName): string {
  return `iamu://schemas/${name}`;
}

export const SCHEMA_INDEX_URI = "iamu://schemas/index";

/** Build the index payload served at `iamu://schemas/index`. */
export function buildSchemaIndex(): {
  version: number;
  schemas: Array<{
    name: SchemaName;
    uri: string;
    description: string;
  }>;
} {
  const descriptions: Record<SchemaName, string> = {
    AuthorityLevel: "Source authority levels (legislation > regulation > ...)",
    FindingType:
      "Finding types (helps | hinders | missing | irrelevant | incorrect)",
    MirrorStatus: "Cloud mirror state for local-first entities",
    FindingSummary: "Shape of one finding row in list_findings responses",
    ScenarioSummary: "Shape of one scenario row in list_scenarios responses",
    PersonaSummary: "Shape of one persona row in list_personas responses",
    OutlineSummary: "Shape of one outline row in list_outlines responses",
    CapabilitySummary:
      "Shape of one capability row in list_capabilities responses",
    GetConstraintsData: "Envelope data for get_constraints",
    QueryData: "Envelope data for query (knowledge-base search)",
    ValidateData: "Envelope data for validate (compliance checking)",
    ListSourcesData: "Envelope data for list_sources (knowledge base coverage)",
    ListFindingsData: "Envelope data for list_findings (paginated)",
    ListScenariosData: "Envelope data for list_scenarios (paginated)",
    GetProjectData: "Envelope data for get_project",
    CreateScenarioData: "Envelope data for create_scenario (binding state)",
    PrepareWalkthroughData: "Envelope data for prepare_walkthrough",
    ListPersonasData: "Envelope data for list_personas (paginated)",
    ListOutlinesData: "Envelope data for list_outlines (paginated)",
    ListCapabilitiesData: "Envelope data for list_capabilities (paginated)",
    GetPersonaData: "Envelope data for get_persona (full body)",
    GetOutlineData: "Envelope data for get_outline (full body)",
    GetFindingData: "Envelope data for get_finding (full body)",
    CreatePersonaData: "Envelope data for create_persona (binding state)",
    CreateOutlineData: "Envelope data for create_outline (binding state)",
    CreateCapabilityData: "Envelope data for create_capability (binding state)",
    RecordStepData: "Envelope data for record_step",
    RecordFindingData:
      "Envelope data for record_finding (echoes persisted finding)",
    CompleteWalkthroughData:
      "Envelope data for complete_walkthrough (CanonicalWalkthrough document)",
    GetJudgeInputData:
      "Envelope data for get_judge_input (judge bundle for subagent)",
    RecordJudgeResultData: "Envelope data for record_judge_result",
  };

  return {
    version: 1,
    schemas: (Object.keys(SCHEMA_REGISTRY) as SchemaName[]).map((name) => ({
      name,
      uri: schemaResourceUri(name),
      description: descriptions[name],
    })),
  };
}
